// The embed postMessage contract, version 1 (Frontend §4.3; Security §6.5). Shared by embed.js (the host side, this
// folder) and the iframe side (`src/features/frame-bridge`). Messages are UI events only: never patient data.

export const PA_NS = 'pa'
/** The protocol version this build speaks. The iframe also accepts `v - 1` (embed.js is cached for 5 minutes). */
export const PROTOCOL_VERSION = 1

interface PaMsgBase {
  ns: typeof PA_NS
  v: typeof PROTOCOL_VERSION
}

/** host (embed.js) → iframe, sent with targetOrigin = PATIENT_ORIGIN. The iframe accepts only these two. */
export type HostToFrame = PaMsgBase & ({ type: 'init'; nonce: string } | { type: 'close' })

export type StepLayout = 'panel' | 'overlay'

/** iframe → host, sent with targetOrigin = the hostOrigin captured from `init`, never `'*'`. */
export type FrameToHost = PaMsgBase &
  (
    | { type: 'ready'; nonce: string }
    | { type: 'resize'; height: number }
    | { type: 'step'; layout: StepLayout; booked?: true }
    | { type: 'close' }
    | { type: 'closed' }
  )

/** embed.js: no `ready` within this long (including a `frame-ancestors` refusal) → "Booking couldn't load." */
export const READY_TIMEOUT_MS = 10_000
/** embed.js: the iframe is removed this long after `close` even without `closed`. */
export const CLOSE_FALLBACK_MS = 800
/** The iframe: no valid `init` within this long → the app still works but sends no messages (and starts its visit). */
export const INIT_WAIT_MS = 5_000

/** Any `{ ns: 'pa', v: number, type: string }` object (the version is checked by each side). */
export function isPaMessage(data: unknown): data is { ns: 'pa'; v: number; type: string; [k: string]: unknown } {
  if (typeof data !== 'object' || data === null) return false
  const d = data as { ns?: unknown; v?: unknown; type?: unknown }
  return d.ns === PA_NS && typeof d.v === 'number' && typeof d.type === 'string'
}

/** `?src=…&p=…` for the frame and direct paths: only the parts in use (`p` = the C-02 link preset, task 22). */
function queryOf(source: string | null, preset: string | null): string {
  const parts: string[] = []
  if (source !== null && source !== '') parts.push(`src=${encodeURIComponent(source)}`)
  if (preset !== null && preset !== '') parts.push(`p=${encodeURIComponent(preset)}`)
  return parts.length > 0 ? `?${parts.join('&')}` : ''
}

/**
 * The frame's path for a clinic (D-36): `/c/{slug}/embed?src={source}&p={preset}` (each only when set; `p` from
 * `data-preset`, D-79).
 */
export function embedPath(slug: string, source: string | null, preset: string | null = null): string {
  return `/c/${encodeURIComponent(slug)}/embed${queryOf(source, preset)}`
}

/** The direct link for a clinic: `/c/{slug}?src={source}&p={preset}` (the fallbacks open it top-level, in a new tab). */
export function directPath(slug: string, source: string | null, preset: string | null = null): string {
  return `/c/${encodeURIComponent(slug)}${queryOf(source, preset)}`
}
